/**
 * writeBackBridge — bidirectional sync between the live Word doc and
 * the batch SSOT.
 *
 * Phase 4 of batch-SSOT authoring. The brief: "Live fill data should
 * always just be derived from this whenever an update is done in a
 * section in the live fill document, this should also be stored in
 * the batch data, but only after the batch draft has been inserted
 * into the live fill."
 *
 * Two producers, one endpoint (``submitSectionEdit``):
 *   - Manual user edits  → ``source: "user"``
 *   - AI re-drafts       → ``source: "agent_redraft"``
 *
 * Concurrency: optimistic via ``expected_version``. The caller passes
 * the ``section_version`` last observed; a 409 response means a
 * concurrent write landed first and the client should reconcile (see
 * ``handleEditConflict`` below).
 *
 * Pre-insert guardrail: the server rejects edits to sections that
 * have not reached ``inserted`` status. Callers don't need to gate —
 * the server is authoritative.
 */

import {
  bulkSubmitEdits,
  getAllSectionContent,
  getSectionContent,
  submitSectionEdit,
  updateTitleFields,
  uploadSectionFigure,
  type RetrievalProvenanceItem,
  type SectionLifecycleStatus,
} from "../../api/client";
import { buildSectionMarkdown, type CapturedSection } from "../../services/sectionMarkdown";
import {
  capturedPictureCount,
  isChangeTrackingOn,
  readSectionsForSave,
  readTitlePageFields,
  stampSectionFigures,
} from "../../services/wordWriter";

// ---------------------------------------------------------------------------
// Public bridge for AI re-drafts (used by the existing re-draft path
// the moment its content lands in the live Word doc).
// ---------------------------------------------------------------------------

export interface AgentRedraftResult {
  ok: boolean;
  newVersion?: number;
  conflict?: {
    currentVersion: number;
    currentContent: string | null;
  };
  error?: string;
}

/**
 * Tell the SSOT that an AI re-draft has replaced the content of a
 * section. Returns a result discriminated by ``ok``. Conflict + error
 * are surfaced so the caller can resolve (e.g. re-fetch the latest
 * SSOT version and retry, or surface a user-visible warning).
 */
export async function submitAgentRedraft(params: {
  runId: string;
  moduleId: string;
  content: string;
  expectedVersion?: number;
  sectionNumber?: string | null;
  /** Retrieval provenance to append to the section on this edit. */
  transactions?: RetrievalProvenanceItem[];
  /** The rewrite agent's tool calls + root span — Action History attribution. */
  toolCalls?: Record<string, unknown>[];
  rootSpanId?: string | null;
  /** Chat edit-highlight trigger (user instruction + highlighted text) — heads
   *  the edit's subtree on Action History. */
  instruction?: string | null;
  selectedText?: string | null;
}): Promise<AgentRedraftResult> {
  try {
    const res = await submitSectionEdit({
      runId: params.runId,
      moduleId: params.moduleId,
      content: params.content,
      source: "agent_redraft",
      expectedVersion: params.expectedVersion,
      sectionNumber: params.sectionNumber,
      transactions: params.transactions,
      toolCalls: params.toolCalls,
      rootSpanId: params.rootSpanId,
      instruction: params.instruction,
      selectedText: params.selectedText,
      editKind: "approved_proposal",
    });
    return { ok: true, newVersion: res.section_version };
  } catch (err) {
    return parseEditError(err);
  }
}

// ---------------------------------------------------------------------------
// Manual-edit watcher — Office.js integration point.
// ---------------------------------------------------------------------------

/**
 * Subscribe to manual edits on an inserted section and forward them
 * to the SSOT.
 *
 * IMPLEMENTATION NOTE
 * ===================
 * This stub deliberately does not yet wire the Office.js change
 * listener. The full implementation needs to:
 *
 *   1. Locate the content control / bookmarked range that wraps the
 *      section's body. The current insertSectionContent path writes
 *      paragraphs into a bookmarked range identified by section
 *      number — that bookmark is the anchor.
 *   2. Use ``Word.Document.contentControls`` (if the inserter wraps
 *      each section in a content control) or
 *      ``range.track()`` + a polling diff (if not) to detect changes.
 *      A debounced (~2s) change handler reads the current text and
 *      forwards it to ``submitSectionEdit({source: "user"})``.
 *   3. Carry the last-seen ``section_version`` so the
 *      ``expected_version`` check can detect cross-window conflicts.
 *
 * Why this isn't implemented inline: the existing wordWriter +
 * useWordEdits paths already have substantial Office.js machinery for
 * change attribution (agent-vs-user). The right integration is to
 * graft this onto useWordEdits' existing user-edit detection rather
 * than build a parallel watcher. Doing that safely requires the user
 * to walk through the existing flow once.
 *
 * For now the function exists so the rest of the system can call it
 * unconditionally — it just no-ops until the wiring lands.
 */
export function registerSectionEditWatcher(opts: {
  runId: string;
  moduleId: string;
  sectionNumber: string;
  onConflict?: (current: { version: number; content: string | null }) => void;
}): () => void {
  // No-op cleanup function. When the full watcher lands, return a
  // disposer that detaches the change listener.
  void opts;
  return () => {
    /* no-op */
  };
}

/**
 * Forward a single manual edit to the SSOT. Call this from the
 * (future) change handler that ``registerSectionEditWatcher`` will
 * install, OR from any other code path that observes a user edit on
 * an already-inserted section.
 */
export async function submitManualEdit(params: {
  runId: string;
  moduleId: string;
  content: string;
  expectedVersion?: number;
}): Promise<AgentRedraftResult> {
  try {
    const res = await submitSectionEdit({
      runId: params.runId,
      moduleId: params.moduleId,
      content: params.content,
      source: "user",
      expectedVersion: params.expectedVersion,
      editKind: "manual_save",
    });
    return { ok: true, newVersion: res.section_version };
  } catch (err) {
    return parseEditError(err);
  }
}

// ---------------------------------------------------------------------------
// Error parsing — translate the server's 409 / 422 payloads into the
// AgentRedraftResult union.
// ---------------------------------------------------------------------------

function parseEditError(err: unknown): AgentRedraftResult {
  // The shared ``request()`` helper throws an Error whose ``.message``
  // is the JSON-decoded ``detail`` field. For VersionConflict the
  // server returns:
  //   { detail: { error: "version_conflict", current_version, current_content } }
  // The default error path stringifies the structured detail; we
  // try to recover the shape if possible.
  if (err instanceof Error) {
    try {
      const parsed = JSON.parse(err.message) as {
        error?: string;
        current_version?: number;
        current_content?: string | null;
      };
      if (parsed.error === "version_conflict" && typeof parsed.current_version === "number") {
        return {
          ok: false,
          conflict: {
            currentVersion: parsed.current_version,
            currentContent: parsed.current_content ?? null,
          },
        };
      }
    } catch {
      // Fall through to generic error
    }
    return { ok: false, error: err.message };
  }
  return { ok: false, error: String(err) };
}

// ---------------------------------------------------------------------------
// Spine display helpers shared by the watcher's future caller + the
// AI re-draft path.
// ---------------------------------------------------------------------------

export function isWriteBackEligible(status: SectionLifecycleStatus): boolean {
  // Mirror of ``server.section_state.post_insert_states``.
  return status === "inserted" || status === "edited";
}

// ---------------------------------------------------------------------------
// Save-button capture — live Word doc → Mongo SSOT (2026-07-14)
// ---------------------------------------------------------------------------
//
// "When the user hits Save, the latest from the drafted output gets updated
// to the source of truth directly." No Redis / hot-cache intermediary: each
// changed section's live Word text goes straight to
// ``POST /runs/{id}/sections/{mid}/edit`` (→ ``section_state.transition`` →
// Mongo ``draft_full`` + issue-ledger reconcile, so markers the writer
// deleted in Word are recorded as resolved-by-writer).
//
// Markdown-only (2026-08-05): Save persists ONLY the markdown SSOT
// (``draft_full``). The formatting-fidelity overlay (``section_ooxml``, captured
// via ``Range.getOoxml()``) was the single biggest client-side cost and the main
// Save hang; it is not load-bearing — the download rebuild always falls back to
// the markdown render — so it was dropped. Downloads render edited sections from
// markdown; sections the writer never touched keep whatever OOXML they already
// have server-side (stale rows go dormant once a section's version bumps).
//
// Changed-sections-only (user decision): unchanged sections are skipped so
// Save doesn't bump versions, churn the ledger, or flatten the markdown of
// sections the writer never touched. Last-write-wins (no expected_version):
// the doc is what the writer saved. Persisted section-by-section (one ``/edit``
// per changed section through a small pool) so the writer sees real per-section
// progress and one section's failure never blocks the rest.

export interface CaptureSaveResult {
  saved: number;
  /** Total skips (unchanged + missing) — kept for legacy consumers. */
  skipped: number;
  /** Section content matched the SSOT (benign no-op, incl. title page). */
  skippedUnchanged: number;
  /** Section not found in the open doc (never inserted / heading renamed) —
   *  the main "my save didn't take" confusion; surfaced by the Save UI. */
  skippedMissing: number;
  /** Section changed but Track Changes is ON, so ``paragraph.text`` can fold in
   *  deleted (struck-through) text. Without an OOXML capture to derive the
   *  accepted text we can't persist clean markdown, so the section is skipped
   *  rather than saving revision-polluted text. Saves normally once Track
   *  Changes is off (or the edits are accepted/rejected). */
  skippedTrackChanges: number;
  /** A picture the writer inserted is still loading in Word, so its bytes
   *  can't be stored yet — the section is left unsaved (saving now would drop
   *  the figure) and saves on the next attempt. */
  skippedFigurePending: number;
  /** Section numbers behind ``skippedTrackChanges`` / ``skippedMissing`` — so
   *  the auto-save indicator can name what it could not save. */
  skippedTrackChangesSections: string[];
  skippedMissingSections: string[];
  failed: number;
  /** Section numbers whose markdown save failed — for the error toast. */
  failedSections: string[];
  /** Sections somebody else changed between this Save's fetch and its write.
   *
   *  Kept apart from ``failed`` because the two need different words. A failure
   *  says *try again*; this says *somebody else's work is in there, and yours is
   *  still in this document*. Telling a writer to retry a conflict is how the
   *  other edit gets destroyed on the second attempt. */
  conflicted: number;
  conflictedSections: string[];
  /** True when the authoritative all-sections fetch itself failed — nothing
   *  was compared, so "0 saved" must NOT read as "nothing to save". */
  fetchFailed: boolean;
  /** True when the Word-side document capture threw (e.g. a RichApi
   *  ``InvalidArgument`` while reading section bodies) — the failure never
   *  reached the server, so it must NOT surface as "couldn't reach the server".
   *  Distinct from ``fetchFailed`` (which is a real network/SSOT-fetch failure). */
  captureFailed: boolean;
}

function _emptyCaptureResult(): CaptureSaveResult {
  return {
    saved: 0,
    skipped: 0,
    skippedUnchanged: 0,
    skippedMissing: 0,
    skippedTrackChanges: 0,
    skippedFigurePending: 0,
    skippedTrackChangesSections: [],
    skippedMissingSections: [],
    failed: 0,
    failedSections: [],
    conflicted: 0,
    conflictedSections: [],
    fetchFailed: false,
    captureFailed: false,
  };
}

/** Formatting-insensitive content identity. Word round-trips markdown as
 *  plain text (``**bold**`` → bold, ``## 9.1`` heading line → styled heading
 *  paragraph), so a formatting-aware diff would flag every section as
 *  changed and flatten untouched markdown. Collapsing to lowercase
 *  alphanumerics keeps real edits (words/numbers added, removed, reworded)
 *  while ignoring markup-only differences. */
function normalizeForComparison(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/** Split the authoritative markdown into (top heading line, remainder).
 *  ``readSectionsForSave`` captures the section body heading-EXCLUSIVE, so
 *  the baseline must drop its leading ``## N Title`` line to compare like
 *  with like — and the same heading line is re-attached to the submitted
 *  content so the saved ``draft_full`` keeps the ``## N`` shape that
 *  ``splitDraftBySections`` needs on the next Load. */
function splitLeadingHeading(markdown: string): { heading: string | null; body: string } {
  const trimmed = markdown.replace(/^\s+/, "");
  const firstLineEnd = trimmed.indexOf("\n");
  const firstLine = firstLineEnd === -1 ? trimmed : trimmed.slice(0, firstLineEnd);
  if (/^#{1,6}\s/.test(firstLine)) {
    return {
      heading: firstLine.trim(),
      body: firstLineEnd === -1 ? "" : trimmed.slice(firstLineEnd + 1),
    };
  }
  return { heading: null, body: markdown };
}

const _UPLOADABLE: Record<string, string> = { png: "image/png", jpeg: "image/jpeg", jpg: "image/jpeg", gif: "image/gif" };
/** Largest figure the server stores (``document_assets.MAX_ASSET_BYTES``). */
const _MAX_FIGURE_BYTES = 5 * 1024 * 1024;

function _base64ToBlob(base64: string, mime: string): Blob {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/** The picture as an uploadable image: as-is when the server takes its format
 *  and size, otherwise re-encoded through a canvas (PNG, or JPEG when large). */
async function _figureBlob(base64: string, format: string): Promise<Blob> {
  const mime = _UPLOADABLE[format];
  const raw = _base64ToBlob(base64, mime ?? `image/${format || "png"}`);
  if (mime && raw.size <= _MAX_FIGURE_BYTES) return raw;
  const bitmap = await createImageBitmap(raw);
  const scale = Math.min(1, 2400 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const type = raw.size > _MAX_FIGURE_BYTES ? "image/jpeg" : "image/png";
  const out = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.9));
  if (!out) throw new Error(`could not convert a ${format} figure for upload`);
  return out;
}

type _LiveBody =
  | { status: "ok"; markdown: string }
  | { status: "missing" }
  | { status: "figure_pending" }
  | { status: "upload_failed"; error: string };

/** Turn a captured section into the markdown to save — tables as pipe tables,
 *  figures as ``[IMAGE DATA: …]`` — storing any picture the writer inserted by
 *  hand under its title FIRST. If that upload fails the section must not be
 *  saved (its markdown would point at a figure that isn't stored), so the
 *  caller reports it failed and the writer's picture stays safe in Word.
 *
 *  Uploads run even when the text is unchanged: a picture swapped under the
 *  same caption changes no text, and must still replace the stored figure. */
async function _resolveLiveBody(
  runId: string,
  moduleId: string,
  captured: CapturedSection | null | undefined,
  ssotBody: string,
  withLock: <T>(fn: () => Promise<T>) => Promise<T>,
): Promise<_LiveBody> {
  if (!captured || captured.paragraphs.length === 0) return { status: "missing" };
  const built = buildSectionMarkdown(captured, ssotBody);
  if (built.deferred) return { status: "figure_pending" };
  if (!built.markdown.trim()) return { status: "missing" };
  for (const up of built.uploads) {
    try {
      await uploadSectionFigure({ runId, moduleId, caption: up.caption, image: await _figureBlob(up.base64, up.format) });
    } catch (err) {
      console.warn(`[writeBackBridge] ${captured.sectionNumber}: figure upload failed ("${up.caption}"):`, err);
      return { status: "upload_failed", error: err instanceof Error ? err.message : String(err) };
    }
  }
  if (built.stamps.length > 0) {
    // Best-effort: lets the next Save recognise these pictures without guessing.
    await withLock(() => stampSectionFigures(captured.sectionNumber, built.stamps, capturedPictureCount(captured)));
  }
  return { status: "ok", markdown: built.markdown };
}

/** Capture + persist ONE section's live Word text to the Mongo SSOT
 *  (``draft_full`` markdown), accumulating outcomes into ``result``. Shared by
 *  the whole-doc Save and the section-scoped auto-save: text-changed →
 *  ``submitManualEdit``; unchanged → skip; changed under Track Changes → skip
 *  (can't derive accepted text without an OOXML capture). Best-effort — never
 *  throws. */
async function _captureAndSaveModule(
  runId: string,
  moduleId: string,
  entry: {
    section_number?: string | null;
    draft_content?: string | null;
    /** The version the scoped fetch read this body at — presented back as the
     *  save's guard. Absent on the whole-doc path (it carries ``section_version``
     *  and sends its guard through the bulk request instead). */
    draft_version?: number;
  },
  result: CaptureSaveResult,
): Promise<void> {
  const sectionNumber = entry.section_number;
  // The title page has no numbered heading in the doc; nothing to capture.
  if (!sectionNumber || sectionNumber === "TITLE") {
    result.skipped += 1;
    result.skippedUnchanged += 1;
    return;
  }
  let captured: CapturedSection | null = null;
  try {
    captured = (await readSectionsForSave([sectionNumber])).get(sectionNumber) ?? null;
  } catch (err) {
    console.warn(`[writeBackBridge] readSectionsForSave(${sectionNumber}) failed:`, err);
  }
  const { heading, body } = splitLeadingHeading(entry.draft_content ?? "");
  const resolved = await _resolveLiveBody(runId, moduleId, captured, body, (fn) => fn());
  if (resolved.status === "missing") {
    // Section not present in the open doc (never inserted / heading
    // renamed) — nothing to compare, never overwrite the SSOT blindly.
    result.skipped += 1;
    result.skippedMissing += 1;
    return;
  }
  if (resolved.status === "figure_pending") {
    result.skipped += 1;
    result.skippedFigurePending += 1;
    return;
  }
  if (resolved.status === "upload_failed") {
    result.failed += 1;
    result.failedSections.push(sectionNumber);
    return;
  }
  const live = resolved.markdown;

  const textChanged = normalizeForComparison(body) !== normalizeForComparison(live);
  if (!textChanged) {
    result.skipped += 1;
    result.skippedUnchanged += 1;
    return;
  }

  // Text changed, but under Track Changes ``paragraph.text`` can fold in deleted
  // (struck-through) text. Without an OOXML capture to derive the accepted text
  // we can't persist clean markdown, so skip rather than save revision-polluted
  // text — the section saves normally once Track Changes is off.
  if (await isChangeTrackingOn()) {
    console.warn(`[writeBackBridge] ${sectionNumber}: Track Changes ON — skipped (saves once it's off)`);
    result.skipped += 1;
    result.skippedTrackChanges += 1;
    return;
  }

  const content = heading ? `${heading}\n\n${live}` : live;
  const res = await submitManualEdit({
    runId,
    moduleId,
    content,
    ...(typeof entry.draft_version === "number" ? { expectedVersion: entry.draft_version } : {}),
  });
  if (res.ok) {
    result.saved += 1;
  } else {
    result.failed += 1;
    result.failedSections.push(sectionNumber);
    console.warn(`[writeBackBridge] captureAndSave: save failed for ${moduleId}:`, res.error ?? res.conflict);
  }
}

/** Fetch the authoritative section list for a run. Shared by the whole-doc and
 *  section-scoped save paths. Returns ``null`` on a fetch failure (caller
 *  reports ``fetchFailed``). */
async function _loadSaveContext(runId: string): Promise<{
  entries: Awaited<ReturnType<typeof getAllSectionContent>>;
} | null> {
  try {
    return { entries: await getAllSectionContent(runId) };
  } catch (err) {
    console.warn("[writeBackBridge] captureAndSave: all-sections fetch failed:", err);
    return null;
  }
}

/** Coarse phase + percent for the Save progress card. ``phase`` advances
 *  reading (one shared Word scan) → saving (the single bulk persist request);
 *  ``percent`` (0–100) drives the progress bar. The whole save is one request
 *  now, so there is no per-section counter — the parent eases the bar during the
 *  in-flight request and snaps it to 100 on completion. */
export interface SaveProgress {
  phase: "reading" | "saving";
  percent: number;
}

export interface CaptureSaveOptions {
  /** Serialize the Word-touching capture behind in-flight section inserts.
   *  When provided, ONLY the batch read runs inside it — the network compare
   *  and per-section writes run outside, so the single-writer lock is released
   *  the moment the document scan finishes. Omit for tests / direct use. */
  enqueueWordWrite?: <T>(fn: () => Promise<T>) => Promise<T>;
  onProgress?: (p: SaveProgress) => void;
  /** Section numbers already known to the caller (the live spine list). When
   *  supplied, the Word document scan starts CONCURRENTLY with the SSOT fetch
   *  instead of waiting for it — the two biggest latencies overlap, cutting the
   *  pre-save wait roughly in half. Omit and the scan runs after the fetch
   *  (numbers derived from the fetched entries). TITLE is ignored. */
  knownSectionNumbers?: string[];
}

/** A resolved decision to persist one section's markdown, produced by the
 *  compare pass and consumed by the bounded-concurrency write pool. */
interface PlannedWrite {
  moduleId: string;
  sectionNumber: string;
  content: string;
  /** The version the SSOT body was read at, moments earlier in this same Save.
   *
   *  Undefined only against a server too old to report one. See
   *  ``BulkEditItem.expectedVersion`` for why that must stay distinguishable
   *  from zero. */
  expectedVersion?: number;
}

/** Persist every planned write in ONE ``bulk-edit`` request and map the per-item
 *  outcomes onto the capture counters. The server applies all sections in a
 *  single positional ``bulk_write`` + one ledger reconcile, so the Save no longer
 *  pays N× the client↔server AND server↔Mongo round-trip latency that made large
 *  saves crawl in production (local Mongo hid it: ~0.1ms/round-trip). Per-item
 *  results keep failure isolation — a stale/invalid section fails in place.
 *  Best-effort: never throws; a whole-request failure marks every write failed. */
async function _persistWritesBulk(runId: string, writes: PlannedWrite[], result: CaptureSaveResult): Promise<void> {
  const byModuleNum = new Map(writes.map((w) => [w.moduleId, w.sectionNumber]));
  try {
    const { results: itemResults } = await bulkSubmitEdits({
      runId,
      source: "user",
      edits: writes.map((w) => ({
        moduleId: w.moduleId,
        kind: "text" as const,
        content: w.content,
        // **The guard this Save never presented.** Every write here used to go
        // out with no `expected_version`, which is last-write-wins: a web client
        // editing between this Save's own fetch and its write was silently
        // overwritten, and no conflict was raised anywhere because the server
        // had not been asked to check for one.
        //
        // The add-in subscribes to nothing, so it cannot learn about a
        // colleague's edit as it happens. This does not fix that. What it fixes
        // is the half that was never a limitation of Word at all: the fetch was
        // already here and already fresh, and its version was simply thrown away.
        ...(w.expectedVersion !== undefined ? { expectedVersion: w.expectedVersion } : {}),
      })),
    });
    for (const r of itemResults) {
      const label = byModuleNum.get(r.module_id ?? "") ?? r.module_id ?? "?";
      if (r.status === "applied") {
        result.saved += 1;
      } else if (r.status === "conflict") {
        // Somebody else wrote this section between this Save's fetch and its
        // write. Counted apart from a failure because it is a different event
        // with a different answer: a failure means try again, and this means
        // somebody else's work is in there and yours is still in the document.
        // Reporting it as a generic failure would send the writer to retry —
        // which, before the guard existed, is exactly what would have destroyed
        // the other edit.
        result.conflicted += 1;
        result.conflictedSections.push(label);
        console.warn(
          `[writeBackBridge] ${r.module_id} changed since this Save read it ` +
            `(server at v${String(r.current_version)}) — not overwritten`,
        );
      } else {
        result.failed += 1;
        result.failedSections.push(label);
        console.warn(`[writeBackBridge] save not applied for ${r.module_id}:`, r.status, r.reason);
      }
    }
  } catch (err) {
    // The single request failed outright — every changed section is unsaved.
    console.warn("[writeBackBridge] bulk save failed:", err);
    for (const w of writes) {
      result.failed += 1;
      result.failedSections.push(w.sectionNumber);
    }
  }
}

/** Capture the live Word content of every inserted section and persist the
 *  CHANGED ones straight to the Mongo SSOT. Returns per-outcome counts.
 *  Wired to the Save button (full-document sweep).
 *
 *  Performance: the whole document is scanned ONCE (``readSectionsForSave``,
 *  structural: tables + figures, no OOXML) rather than re-scanned per section, and the
 *  per-section ``/edit`` writes run in a bounded-concurrency pool OUTSIDE the
 *  Word write lock, reporting progress as each lands. Best-effort per section:
 *  one failure never blocks the rest. */
export async function captureAndSaveSections(runId: string, opts: CaptureSaveOptions = {}): Promise<CaptureSaveResult> {
  const result = _emptyCaptureResult();
  const { enqueueWordWrite, onProgress } = opts;

  // Save instrumentation. Each phase is timed with performance.now() and a
  // single structured summary is logged at the end so a slow Save is
  // attributable to network fetch / Word read / compare / persist. Cheap; safe
  // to leave on.
  const _t = {
    start: performance.now(),
    loadContextMs: 0,
    wordReadMs: 0,
    scanMs: 0,
    titleMs: 0,
    compareMs: 0,
    persistMs: 0,
  };

  // Step logger: prints each Save phase with its elapsed-since-start time so a
  // slow Save is visible AS IT HAPPENS in the console (the structured summary at
  // the end stays too). Only the Save button uses this path — auto-save uses the
  // scoped single-section helpers — so it won't spam on keystrokes.
  // Braced body, not a concise arrow: with `=> console.info(...)` the call
  // IS the arrow's return value, so esbuild's `pure` annotation (see
  // vite.config.ts) cannot drop it and these timings print in production.
  // As a statement its value is unused and the whole call is stripped.
  const _step = (msg: string): void => {
    console.info(`[Save·client +${Math.round(performance.now() - _t.start)}ms] ${msg}`);
  };
  _step(`begin (run ${runId})`);

  const withLock = <T>(fn: () => Promise<T>): Promise<T> => (enqueueWordWrite ? enqueueWordWrite(fn) : fn());

  // Read the plain-text bodies of ``nums`` + the Track-Changes flag in ONE Word
  // scan, held behind the write lock so it can't interleave with in-flight
  // section inserts. (Under Track Changes ``paragraph.text`` can fold in deleted
  // text; without an OOXML capture to derive the accepted text we skip changed
  // sections in the compare pass below.)
  const readBodies = (nums: string[]): Promise<{ bodies: Map<string, CapturedSection | null>; trackChangesOn: boolean }> =>
    withLock(async () => {
      const _s = performance.now();
      // Structural capture — tables + figures, not just paragraph text — so a
      // Save can never flatten a table or drop a figure.
      const bodies = await readSectionsForSave(nums);
      const trackChangesOn = await isChangeTrackingOn();
      _t.scanMs += performance.now() - _s;
      return { bodies, trackChangesOn };
    });

  // Advance the card to "Reading your document" IMMEDIATELY so "Preparing" only
  // covers the instant setup — the fetch + Word scan below both run under it.
  const hintNumbers = (opts.knownSectionNumbers ?? []).filter((n) => n && n !== "TITLE");
  onProgress?.({ phase: "reading", percent: 30 });
  _step(
    hintNumbers.length > 0
      ? `reading: GET /all-sections ‖ Word scan of ${hintNumbers.length} sections (concurrent)`
      : "reading: GET /all-sections, then Word scan (no spine hint)",
  );

  // 1. Fetch the SSOT bodies AND (when the caller already knows the section
  //    numbers from the live spine) scan the Word document CONCURRENTLY, so the
  //    two biggest latencies overlap. Without the hint, fall back to
  //    fetch-then-scan (numbers derived from the fetched entries below).
  const _tLoad = performance.now();
  let prefetch: { bodies: Map<string, CapturedSection | null>; trackChangesOn: boolean } | null = null;
  let ctx: Awaited<ReturnType<typeof _loadSaveContext>>;
  try {
    if (hintNumbers.length > 0) {
      const [c, pre] = await Promise.all([_loadSaveContext(runId), readBodies(hintNumbers)]);
      ctx = c;
      prefetch = pre;
    } else {
      ctx = await _loadSaveContext(runId);
    }
  } catch (err) {
    // The concurrent Word scan failed (``_loadSaveContext`` itself never throws,
    // so this can only be the Word capture). Nothing was compared — surface it as
    // a CAPTURE failure, not a server-reachability failure: the request never
    // left the client.
    console.warn("[writeBackBridge] captureAndSaveSections: Word capture failed:", err);
    result.captureFailed = true;
    return result;
  }
  _t.loadContextMs = performance.now() - _tLoad;
  if (ctx === null) {
    _step("fetch FAILED (GET /all-sections unreachable) — aborting");
    result.fetchFailed = true;
    return result;
  }
  const { entries } = ctx;
  _step(
    `fetch + scan done in ${Math.round(_t.loadContextMs)}ms ` +
      `(${entries.length} SSOT sections, Word scan ${Math.round(_t.scanMs)}ms)`,
  );

  // One entry per module post-SSOT; keep the first defensively if not.
  const byModule = new Map<string, (typeof entries)[number]>();
  for (const entry of entries) {
    if (entry.module_id && !byModule.has(entry.module_id)) {
      byModule.set(entry.module_id, entry);
    }
  }

  // Body targets (numbered sections). The title page has no numbered heading;
  // it's captured separately from its field table — see ``titleTarget``.
  const targets: { moduleId: string; sectionNumber: string; entry: (typeof entries)[number] }[] = [];
  let titleTarget: { moduleId: string; knownKeys: string[]; entry: (typeof entries)[number] } | null = null;
  for (const [moduleId, entry] of byModule) {
    const sn = entry.section_number;
    if (sn === "TITLE") {
      const knownKeys = Object.keys(entry.title_fields ?? {});
      if (knownKeys.length > 0) {
        titleTarget = { moduleId, knownKeys, entry };
      } else {
        result.skipped += 1;
        result.skippedUnchanged += 1;
      }
      continue;
    }
    if (!sn) {
      result.skipped += 1;
      result.skippedUnchanged += 1;
      continue;
    }
    targets.push({ moduleId, sectionNumber: sn, entry });
  }
  const sectionNumbers = targets.map((t) => t.sectionNumber);

  // 2. Resolve the body scan: reuse the concurrent prefetch when it covers every
  //    target; otherwise scan the actual target numbers now (hint absent, or a
  //    target the spine didn't list). The title-page field read needs the field
  //    keys from the SSOT, so it always runs after the fetch.
  let captured: {
    bodies: Map<string, CapturedSection | null>;
    titleFields: Record<string, string> | null;
    trackChangesOn: boolean;
  };
  const _tRead = performance.now();
  try {
    // Reuse the concurrent prefetch and scan ONLY the target numbers it didn't
    // cover (a module the spine hint lacked / a renamed number). This used to
    // discard the prefetch and re-scan EVERY target on any miss — a silent
    // second whole-document Word read on top of the first.
    let scan: { bodies: Map<string, CapturedSection | null>; trackChangesOn: boolean };
    if (prefetch === null) {
      scan = await readBodies(sectionNumbers);
    } else {
      const covered = prefetch;
      const missing = sectionNumbers.filter((n) => !covered.bodies.has(n));
      if (missing.length === 0) {
        scan = covered;
      } else {
        _step(`spine hint missed ${missing.length} section(s) — scanning just those: ${missing.join(", ")}`);
        const extra = await readBodies(missing);
        scan = {
          bodies: new Map([...covered.bodies, ...extra.bodies]),
          trackChangesOn: covered.trackChangesOn || extra.trackChangesOn,
        };
      }
    }
    let titleFields: Record<string, string> | null = null;
    if (titleTarget) {
      const tt = titleTarget;
      try {
        const _tTitle = performance.now();
        titleFields = await withLock(() => readTitlePageFields(tt.knownKeys));
        _t.titleMs = performance.now() - _tTitle;
      } catch (err) {
        console.warn("[writeBackBridge] title-page capture failed:", err);
      }
    }
    captured = { bodies: scan.bodies, titleFields, trackChangesOn: scan.trackChangesOn };
    _t.wordReadMs = performance.now() - _tRead;
  } catch (err) {
    // The shared document scan failed — nothing was compared. This is a Word-side
    // CAPTURE failure (the server was never contacted), so it must NOT read as
    // "couldn't reach the server".
    console.warn("[writeBackBridge] captureAndSaveSections: Word capture failed:", err);
    result.captureFailed = true;
    return result;
  }

  // 3. Compare against the SSOT + collect the changed sections (pure).
  const _tCompare = performance.now();
  const writes: PlannedWrite[] = [];
  for (const t of targets) {
    const { heading, body } = splitLeadingHeading(t.entry.draft_content ?? "");
    const resolved = await _resolveLiveBody(runId, t.moduleId, captured.bodies.get(t.sectionNumber), body, withLock);
    if (resolved.status === "missing") {
      // Section not present in the open doc (never inserted / heading renamed) —
      // never overwrite the SSOT blindly.
      result.skipped += 1;
      result.skippedMissing += 1;
      result.skippedMissingSections.push(t.sectionNumber);
      continue;
    }
    if (resolved.status === "figure_pending") {
      console.warn(`[writeBackBridge] ${t.sectionNumber}: a figure is still loading in Word — skipped (saves next time)`);
      result.skipped += 1;
      result.skippedFigurePending += 1;
      continue;
    }
    if (resolved.status === "upload_failed") {
      result.failed += 1;
      result.failedSections.push(t.sectionNumber);
      continue;
    }
    const live = resolved.markdown;

    const textChanged = normalizeForComparison(body) !== normalizeForComparison(live);
    if (!textChanged) {
      result.skipped += 1;
      result.skippedUnchanged += 1;
      continue;
    }

    // Changed, but under Track Changes we can't derive clean accepted text from
    // ``paragraph.text`` alone — skip rather than persist revision-polluted
    // markdown (saves once Track Changes is off / edits are accepted).
    if (captured.trackChangesOn) {
      console.warn(`[writeBackBridge] ${t.sectionNumber}: Track Changes ON — skipped (saves once it's off)`);
      result.skipped += 1;
      result.skippedTrackChanges += 1;
      result.skippedTrackChangesSections.push(t.sectionNumber);
      continue;
    }

    const content = heading ? `${heading}\n\n${live}` : live;
    writes.push({
      moduleId: t.moduleId,
      sectionNumber: t.sectionNumber,
      content,
      // The version this very Save read, a few hundred milliseconds ago. The
      // fetch was always here; the version was simply not in the response and
      // so was never presented back.
      ...(t.entry.section_version !== undefined ? { expectedVersion: t.entry.section_version } : {}),
    });
  }
  _t.compareMs = performance.now() - _tCompare;
  const _payloadBytesOut = writes.reduce((n, w) => n + w.content.length, 0);
  _step(
    `compared: ${writes.length} changed, ${result.skippedUnchanged} unchanged, ` +
      `${result.skippedMissing} missing, ${result.skippedTrackChanges} track-changes`,
  );

  // 4. Persist ALL changed sections in ONE bulk request (network; outside the
  //    Word lock). The single request gives no per-section acks, so the parent
  //    eases the progress bar during it and snaps to 100 on completion.
  const _tPersist = performance.now();
  onProgress?.({ phase: "saving", percent: 65 });
  if (writes.length > 0) {
    _step(`POST /sections/bulk-edit — ${writes.length} sections, ${_payloadBytesOut} bytes (awaiting server)…`);
    await _persistWritesBulk(runId, writes, result);
    _step(
      `bulk-edit returned in ${Math.round(performance.now() - _tPersist)}ms: ` +
        `saved=${result.saved} failed=${result.failed}`,
    );
  } else {
    _step("nothing changed — no bulk request sent");
  }
  _t.persistMs = performance.now() - _tPersist;

  // 5. Title page — the field table renders from ``title_fields`` (not
  //    ``draft_full``), so it's captured + diffed separately. Persist only the
  //    fields whose live value differs from the SSOT (server merges the rest).
  if (titleTarget && captured.titleFields) {
    const ssot = titleTarget.entry.title_fields ?? {};
    const changed: Record<string, string> = {};
    for (const [k, v] of Object.entries(captured.titleFields)) {
      if (v.trim() !== String(ssot[k] ?? "").trim()) changed[k] = v;
    }
    if (Object.keys(changed).length > 0) {
      try {
        await updateTitleFields({ runId, moduleId: titleTarget.moduleId, titleFields: changed });
        result.saved += 1;
      } catch (err) {
        result.failed += 1;
        result.failedSections.push("Title page");
        console.warn("[writeBackBridge] title-fields save failed:", err);
      }
    } else {
      result.skipped += 1;
      result.skippedUnchanged += 1;
    }
  }

  const _round = (n: number): number => Math.round(n);
  let _payloadBytes = 0;
  for (const w of writes) _payloadBytes += w.content.length;
  console.info("[writeBackBridge] save timing", {
    runId,
    sectionsTotal: targets.length,
    textWrites: writes.length,
    saved: result.saved,
    payloadBytes: _payloadBytes,
    loadContextMs: _round(_t.loadContextMs),
    wordReadMs: _round(_t.wordReadMs),
    scanMs: _round(_t.scanMs),
    titleMs: _round(_t.titleMs),
    compareMs: _round(_t.compareMs),
    persistMs: _round(_t.persistMs),
    totalMs: _round(performance.now() - _t.start),
  });
  _step(`complete — total ${_round(performance.now() - _t.start)}ms`);

  return result;
}

/** Fetch ONE module's SSOT body for the scoped auto-save.
 *
 *  Uses the per-section route (``GET /runs/{id}/sections/{module_id}`` — a
 *  projected single-module read) instead of ``/all-sections``. The scoped save
 *  used to share ``_loadSaveContext`` with the whole-doc Save and so fetched
 *  every section (~17 s on a 90-section run) to hash-compare ONE — and the
 *  watcher fired that on a mere caret move. Returns ``null`` when the module is
 *  not in the SSOT (never drafted / unknown id) and ``"failed"`` when the fetch
 *  itself errored, so the caller can tell "nothing to do" from "unreachable". */
async function _loadOneSection(
  runId: string,
  moduleId: string,
): Promise<{ section_number?: string | null; draft_content?: string | null; draft_version?: number } | null | "failed"> {
  let sr: Awaited<ReturnType<typeof getSectionContent>>;
  try {
    sr = await getSectionContent(runId, moduleId);
  } catch (err) {
    const status = (err as { status?: number } | null)?.status;
    // 404 = the module isn't in this run's SSOT — a no-op, not a failure.
    if (status === 404) return null;
    console.warn("[writeBackBridge] scoped save: section fetch failed:", err);
    return "failed";
  }
  const sec = sr?.sections?.[0];
  if (!sec) return null;
  return { section_number: sec.section_number, draft_content: sec.draft_content, draft_version: sec.draft_version };
}

/** Capture + persist a SINGLE section (by dotted section number) to the SSOT.
 *  Drives auto-save (on text insert) so a keystroke persists just its own
 *  section — the cheap, scoped counterpart to ``captureAndSaveSections``.
 *
 *  ``moduleId`` is the section's module id, resolved by the caller from the live
 *  spine (the watcher only knows the cursor's dotted number). Without it there
 *  is nothing scoped to fetch, so the save is a no-op — it must NEVER fall back
 *  to the whole-document ``/all-sections`` fetch. Best-effort; never throws. */
export async function captureAndSaveOneSection(
  runId: string,
  sectionNumber: string,
  moduleId: string | null | undefined,
): Promise<CaptureSaveResult> {
  const result = _emptyCaptureResult();
  if (!sectionNumber || sectionNumber === "TITLE" || !moduleId) return result;

  const entry = await _loadOneSection(runId, moduleId);
  if (entry === "failed") {
    result.fetchFailed = true;
    return result;
  }
  if (entry === null) return result; // not in the SSOT — nothing to do

  await _captureAndSaveModule(runId, moduleId, entry, result);
  return result;
}

/** Capture + persist a SINGLE section (by module id) to the SSOT. Drives the
 *  post-approval auto-save (highlight edit / full-section proposal), which know
 *  the module id rather than the dotted section number. Best-effort; never
 *  throws. */
export async function captureAndSaveOneModule(runId: string, moduleId: string): Promise<CaptureSaveResult> {
  const result = _emptyCaptureResult();
  if (!moduleId) return result;

  const entry = await _loadOneSection(runId, moduleId);
  if (entry === "failed") {
    result.fetchFailed = true;
    return result;
  }
  if (entry === null) return result; // not in the SSOT — nothing to do

  await _captureAndSaveModule(runId, moduleId, entry, result);
  return result;
}

/** How many single-section SSOT reads ``captureAndSaveModules`` keeps in flight. */
const _SCOPED_FETCH_CONCURRENCY = 6;

/** Capture + persist a SET of sections (by module id): one projected
 *  ``GET /sections/{mid}`` per module (never ``/all-sections``), ONE shared Word
 *  scan, and ONE ``bulk-edit`` for the sections whose text changed. The scoped
 *  counterpart to ``captureAndSaveSections`` — it drives auto-save and the
 *  Issues tabs' save-before-reading-the-ledger, so the ledger is only ever
 *  settled by a body the SSOT actually holds.
 *
 *  ``enqueueWordWrite`` serialises the Word read (and figure stamping) behind
 *  in-flight inserts; the network runs outside it. Best-effort; never throws. */
export async function captureAndSaveModules(
  runId: string,
  moduleIds: string[],
  opts: { enqueueWordWrite?: <T>(fn: () => Promise<T>) => Promise<T> } = {},
): Promise<CaptureSaveResult> {
  const result = _emptyCaptureResult();
  const ids = [...new Set(moduleIds.filter(Boolean))];
  if (!runId || ids.length === 0) return result;
  const withLock = <T>(fn: () => Promise<T>): Promise<T> => (opts.enqueueWordWrite ? opts.enqueueWordWrite(fn) : fn());

  // 1. The SSOT body of each module, a few at a time.
  const loaded = new Map<string, Awaited<ReturnType<typeof _loadOneSection>>>();
  for (let i = 0; i < ids.length; i += _SCOPED_FETCH_CONCURRENCY) {
    const batch = ids.slice(i, i + _SCOPED_FETCH_CONCURRENCY);
    const entries = await Promise.all(batch.map((id) => _loadOneSection(runId, id)));
    batch.forEach((id, j) => loaded.set(id, entries[j]));
  }
  const targets: { moduleId: string; sectionNumber: string; body: string; heading: string | null; version?: number }[] =
    [];
  for (const [moduleId, entry] of loaded) {
    if (entry === "failed") {
      result.fetchFailed = true;
      continue;
    }
    // Not in the SSOT, or the title page (captured from its field table by the
    // whole-document Save only) — nothing to compare here.
    if (entry === null || !entry.section_number || entry.section_number === "TITLE") {
      result.skipped += 1;
      result.skippedUnchanged += 1;
      continue;
    }
    const { heading, body } = splitLeadingHeading(entry.draft_content ?? "");
    targets.push({ moduleId, sectionNumber: entry.section_number, body, heading, version: entry.draft_version });
  }
  if (targets.length === 0) return result;

  // 2. One Word scan for every target section.
  let scan: { bodies: Map<string, CapturedSection | null>; trackChangesOn: boolean };
  try {
    scan = await withLock(async () => ({
      bodies: await readSectionsForSave(targets.map((t) => t.sectionNumber)),
      trackChangesOn: await isChangeTrackingOn(),
    }));
  } catch (err) {
    console.warn("[writeBackBridge] captureAndSaveModules: Word capture failed:", err);
    result.captureFailed = true;
    return result;
  }

  // 3. Compare — same rules as the whole-document Save.
  const writes: PlannedWrite[] = [];
  for (const t of targets) {
    const resolved = await _resolveLiveBody(runId, t.moduleId, scan.bodies.get(t.sectionNumber), t.body, withLock);
    if (resolved.status === "missing") {
      result.skipped += 1;
      result.skippedMissing += 1;
      result.skippedMissingSections.push(t.sectionNumber);
      continue;
    }
    if (resolved.status === "figure_pending") {
      result.skipped += 1;
      result.skippedFigurePending += 1;
      continue;
    }
    if (resolved.status === "upload_failed") {
      result.failed += 1;
      result.failedSections.push(t.sectionNumber);
      continue;
    }
    if (normalizeForComparison(t.body) === normalizeForComparison(resolved.markdown)) {
      result.skipped += 1;
      result.skippedUnchanged += 1;
      continue;
    }
    if (scan.trackChangesOn) {
      result.skipped += 1;
      result.skippedTrackChanges += 1;
      result.skippedTrackChangesSections.push(t.sectionNumber);
      continue;
    }
    writes.push({
      moduleId: t.moduleId,
      sectionNumber: t.sectionNumber,
      content: t.heading ? `${t.heading}\n\n${resolved.markdown}` : resolved.markdown,
      ...(typeof t.version === "number" ? { expectedVersion: t.version } : {}),
    });
  }

  // 4. Every changed section in one request.
  if (writes.length > 0) await _persistWritesBulk(runId, writes, result);
  return result;
}
